import { getText } from '@/lib/i18n/i18n.server';
import { currentLocaleConfig } from '@/lib/i18n/localeSpecific';
import { cn } from '@/lib/utils';

import { Divider } from '@/components/atoms/Divider';
import ImageStrapi, {
  ImageStrapiProps,
} from '@/components/atoms/Image/ImageStrapi';
import { ServiceStatus } from '@/components/atoms/Instatus';
import Section from '@/components/atoms/Section/Section';
import { Text } from '@/components/atoms/Text';
import { TypographyP } from '@/components/ui/typography';

import { getWrapperMaxWidth } from '@/cms/utils';
import { env } from '@/constant/env';
import { SECTION_NAMES } from '@/integrations/trackingScripts/constants';

import FooterCategories, { FooterCategoriesProps } from './FooterCategories';
import FooterContacts, { FooterContactsProps } from './FooterContacts';

export const mutualLinkStyle =
  'text-body-m font-mono text-gray-950 dark:text-base-dark-white hover:text-brand-500 dark:hover:text-brand-500';

export type FooterProps = {
  logo?: ImageStrapiProps;
  contacts?: FooterContactsProps['contacts'];
  categories?: FooterCategoriesProps['categories'];
};

export const Footer = async (props: FooterProps) => {
  const currentYear = new Date().getFullYear();
  const texts = await getText();

  if (!props) {
    return null;
  }

  const shouldShowAddress = env.publicLocale === 'en';
  const hasFooterLinks = !!props.contacts?.length || !!props.categories?.length;

  return (
    <>
      <footer
        className={cn(
          'text-base-white bg-gray-25 dark:bg-gray-dark-975 flex justify-center py-8 md:py-20'
        )}
      >
        <Section
          data-tracker-section={SECTION_NAMES.FOOTER}
          data-tracker-component='footer'
          className={cn(getWrapperMaxWidth('contained'), 'w-full px-5')}
        >
          <div className={cn(hasFooterLinks && 'mb-8 md:mb-12')}>
            <ImageStrapi
              inner={{
                classNames: {
                  image: 'h-5',
                },
              }}
              block={props.logo ?? {}}
            />
          </div>
          <div className='flex flex-col flex-nowrap gap-10 md:flex-row md:gap-20 '>
            <div className='flex h-fit min-w-[200px] flex-col gap-8 md:gap-[60px]'>
              {!!props.contacts?.length && (
                <FooterContacts
                  contacts={props.contacts}
                  classNames={{
                    container: 'gap-8',
                    link: 'gap-4',
                  }}
                />
              )}
              {shouldShowAddress && (
                <div>
                  <TypographyP className='text-label-medium text-gray-500 dark:text-gray-dark-400 font-serif !mt-0'>
                    {texts.footer.headquarters}
                  </TypographyP>
                  <Text className='text-body-m text-gray-950 pt-6'>
                    {texts.footer.address}
                  </Text>
                </div>
              )}
              <ServiceStatus
                size='sm'
                classNames={{
                  // wide enough for longer incident titles (e.g. "ISP Proxies Service Disruption")
                  button: cn(
                    'w-[260px] text-gray-950 dark:text-base-dark-white',
                    props.contacts && !props.contacts.length && 'mt-6'
                  ),
                }}
              />
            </div>
            {!!props.categories?.length && (
              <div className='hidden md:block'>
                <Divider
                  showOnDark
                  alignment='vertical'
                  classNames={{
                    root: 'dark:border-gray-dark-700 border-gray-200',
                  }}
                />
              </div>
            )}
            {!!props.categories?.length && (
              <FooterCategories categories={props.categories} />
            )}
          </div>
        </Section>
      </footer>
      <div className='dark:bg-gray-dark-900 bg-gray-50 py-3 text-center'>
        <TypographyP className='text-body-l text-gray-500 dark:text-gray-dark-400 mt-0'>
          © 2018-{currentYear} {currentLocaleConfig().footerAllRightsReserved}
        </TypographyP>
      </div>
    </>
  );
};
